/**
 * Plate Carrier Konfigurator V2
 * Multi-carrier Alpine.js + Konva.js configurator
 */

const colorAliases = [
  ['multicam', 'multicam original', 'camouflage', 'camo', 'mc'],
  ['olive', 'olive green', 'olive drab', 'od', 'od green'],
  ['coyote', 'coyote tan', 'coyote brown', 'tan', 'fde'],
  ['schwarz', 'black', 'noir'],
  ['ranger green', 'adaptive green'],
  ['grau', 'grey', 'gray', 'wolf grey', 'wolf gray'],
];

function colorMatch(a, b) {
  if (!a || !b) return false;
  const na = a.trim().toLowerCase();
  const nb = b.trim().toLowerCase();
  if (na === nb) return true;
  for (const group of colorAliases) {
    if (group.includes(na) && group.includes(nb)) return true;
  }
  return false;
}

const fallbackColorMap = {
  'multicam': '#6B5B3A',
  'multicam black': '#2A2A2A',
  'multicam tropic': '#3D5A3A',
  'ranger green': '#4A5D23',
  'adaptive green': '#4A5D23',
  'olive': '#556B2F',
  'olive green': '#556B2F',
  'od green': '#3C5A14',
  'coyote': '#8B7355',
  'coyote tan': '#8B7355',
  'coyote brown': '#8B7355',
  'tan': '#D2B48C',
  'schwarz': '#1a1a1a',
  'black': '#1a1a1a',
  'shadow grey': '#5A5A5A',
  'wolf grey': '#7A7A7A',
  'grey': '#808080',
  'grau': '#808080',
  'woodland': '#4A5A2A',
  'flecktarn': '#5A6B3C',
};

function resolveHex(nameOrHex) {
  if (!nameOrHex) return '#888';
  const v = nameOrHex.trim();
  return v.startsWith('#') ? v : (fallbackColorMap[v.toLowerCase()] || '#888');
}

function formatMoney(cents, format) {
  const amount = (cents / 100).toFixed(2);
  const withComma = amount.replace('.', ',');
  return (format || '{{amount_with_comma_separator}} €')
    .replace('{{amount_with_comma_separator}}', withComma)
    .replace('{{amount}}', amount);
}

function registerConfigurator() {
  if (typeof Alpine === 'undefined') return;

  Alpine.data('configurator', () => {
    const data = window.__configuratorData || {};
    const canvasWidth = data.canvasWidth || 800;
    const canvasHeight = data.canvasHeight || 1000;
    const discountPct = data.bundleDiscountPercent || 0;

    return {
      carriers: data.carriers || [],
      attachments: data.attachments || [],
      moneyFormat: data.moneyFormat || '{{amount_with_comma_separator}} €',
      cartAddUrl: data.cartAddUrl || '/cart/add.js',
      addToCartText: data.addToCartText || 'Zum Warenkorb hinzufügen',
      canvasWidth,
      canvasHeight,
      discountPct,

      selectedCarrier: '',
      selectedColor: '',
      selectedSize: '',
      currentView: 'front',
      selectedAttachments: [],
      attachmentColors: {},
      activeZone: '',
      isAdding: false,
      addedSuccess: false,
      cartError: '',

      _stage: null,
      _layer: null,
      _nodeMap: {},
      _imageCache: {},

      init() {
        if (this.carriers.length) this.selectCarrier(this.carriers[0].handle);
        this.$nextTick(() => this.initCanvas());

        let resizeTimer;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => this.fitStageToContainer(), 100);
        });
      },

      // --- Computed ---

      get currentCarrier() {
        return this.carriers.find(c => c.handle === this.selectedCarrier) || null;
      },

      get allZones() {
        return this.currentCarrier ? this.currentCarrier.zones : [];
      },

      get currentZones() {
        return this.allZones.filter(z => z.view === this.currentView);
      },

      get currentColors() {
        if (!this.currentCarrier) return [];
        const seen = new Set();
        return this.currentCarrier.variants.reduce((acc, v) => {
          if (v.option1 && !seen.has(v.option1)) {
            seen.add(v.option1);
            const cd = (this.currentCarrier.colors || []).find(c => c.name === v.option1);
            acc.push({ name: v.option1, hex: resolveHex(cd ? cd.hex : v.option1) });
          }
          return acc;
        }, []);
      },

      get currentSizes() {
        if (!this.currentCarrier) return [];
        const seen = new Set();
        return this.currentCarrier.variants.reduce((acc, v) => {
          if (v.option2 && !seen.has(v.option2)) {
            seen.add(v.option2);
            acc.push(v.option2);
          }
          return acc;
        }, []);
      },

      get filteredAttachments() {
        if (!this.currentCarrier) return [];
        return this.attachments.filter(att => {
          if (att.zone !== this.activeZone) return false;
          if (att.compatibleCarriers && att.compatibleCarriers.length) {
            return att.compatibleCarriers.includes(this.selectedCarrier);
          }
          return true;
        });
      },

      get selectedItems() {
        return this.selectedAttachments
          .map(id => this.attachments.find(a => a.id === id))
          .filter(Boolean);
      },

      get totalPrice() {
        if (!this.currentCarrier) return 0;
        let sum = this.currentCarrier.price;
        for (const att of this.selectedItems) sum += att.price;
        if (this.selectedItems.length > 0 && this.discountPct > 0) {
          sum = Math.round(sum * (1 - this.discountPct / 100));
        }
        return sum;
      },

      // --- Actions ---

      selectCarrier(handle) {
        this.selectedCarrier = handle;
        this.selectedAttachments = [];
        this.attachmentColors = {};
        this.currentView = 'front';
        if (this.currentCarrier) {
          this.selectedColor = this.currentColors.length ? this.currentColors[0].name : '';
          this.selectedSize = this.currentSizes.length ? this.currentSizes[0] : '';
          this.activeZone = this.currentZones.length ? this.currentZones[0].id : '';
          this.$nextTick(() => this.redrawCanvas());
        }
      },

      setColor(name) {
        this.selectedColor = name;
        this.updateBaseImage();
        this.updateAttachmentLayers();
      },

      setSize(size) {
        this.selectedSize = size;
      },

      toggleView() {
        this.currentView = this.currentView === 'front' ? 'back' : 'front';
        const visible = this.currentZones;
        if (!visible.find(z => z.id === this.activeZone)) {
          this.activeZone = visible.length ? visible[0].id : '';
        }
        this.updateBaseImage();
        this.updateAttachmentLayers();
      },

      toggleAttachment(att) {
        const idx = this.selectedAttachments.indexOf(att.id);
        if (idx >= 0) {
          this.selectedAttachments.splice(idx, 1);
        } else {
          const zone = this.allZones.find(z => z.id === att.zone);
          if (zone && zone.only_one) {
            this.selectedAttachments = this.selectedAttachments.filter(id => {
              const existing = this.attachments.find(a => a.id === id);
              return !existing || existing.zone !== att.zone;
            });
          }
          this.selectedAttachments.push(att.id);
        }
        this.$nextTick(() => this.updateAttachmentLayers());
      },

      removeAttachment(id) {
        const idx = this.selectedAttachments.indexOf(id);
        if (idx >= 0) this.selectedAttachments.splice(idx, 1);
        delete this.attachmentColors[id];
        this.$nextTick(() => this.updateAttachmentLayers());
      },

      setAttColor(attId, color) {
        this.attachmentColors[attId] = color;
        this.$nextTick(() => this.updateAttachmentLayers());
      },

      getAttColor(attId) {
        if (this.attachmentColors[attId]) return this.attachmentColors[attId];
        const att = this.attachments.find(a => a.id === attId);
        if (att && att.colors && att.colors.length) {
          const match = att.colors.find(c => colorMatch(c.name, this.selectedColor));
          return match ? match.name : att.colors[0].name;
        }
        return this.selectedColor;
      },

      getAttDisplayImage(att) {
        const color = this.getAttColor(att.id);
        if (att.colors && att.colors.length) {
          const cm = att.colors.find(c => colorMatch(c.name, color));
          if (cm && cm.image) return cm.image;
        }
        if (att.variants && att.variants.length) {
          const v = att.variants.find(va => colorMatch(va.option1, color) && va.image);
          if (v) return v.image;
        }
        return att.overlay || att.thumbnail || '';
      },

      getOverlayUrl(att) {
        // Freigestelltes overlay immer bevorzugen für den Canvas
        if (att.overlay) return att.overlay;
        // Fallback: color-matched variant image or thumbnail
        const color = this.getAttColor(att.id);
        const cm = (att.colors || []).find(c => colorMatch(c.name, color));
        if (cm && cm.image) return cm.image;
        const vm = (att.variants || []).find(v => colorMatch(v.option1, color) && v.image);
        if (vm) return vm.image;
        return att.thumbnail || '';
      },

      isSelected(id) {
        return this.selectedAttachments.includes(id);
      },

      formatPrice(cents) {
        return formatMoney(cents, this.moneyFormat);
      },

      resolveHex(nameOrHex) {
        return resolveHex(nameOrHex);
      },

      // --- Canvas ---

      loadImage(url) {
        if (this._imageCache[url]) return Promise.resolve(this._imageCache[url]);
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => { this._imageCache[url] = img; resolve(img); };
          img.onerror = reject;
          img.src = url;
        });
      },

      initCanvas() {
        const container = this.$refs.canvasContainer;
        if (!container || typeof Konva === 'undefined') return;

        this._stage = new Konva.Stage({
          container,
          width: this.canvasWidth,
          height: this.canvasHeight,
        });
        this._layer = new Konva.Layer();
        this._stage.add(this._layer);
        this._nodeMap = {};

        // Dark background so product photos with light backgrounds blend better
        const bg = new Konva.Rect({
          x: 0, y: 0,
          width: this.canvasWidth,
          height: this.canvasHeight,
          fill: '#111111',
          listening: false,
        });
        this._nodeMap._bg = bg;
        this._layer.add(bg);

        this.fitStageToContainer();
        this.redrawCanvas();
      },

      fitStageToContainer() {
        if (!this._stage) return;
        const cw = this._stage.container().offsetWidth;
        if (!cw) return;
        const scale = cw / this.canvasWidth;
        this._stage.width(this.canvasWidth * scale);
        this._stage.height(this.canvasHeight * scale);
        this._stage.scale({ x: scale, y: scale });
        this._stage.draw();
      },

      async redrawCanvas() {
        if (!this._layer) return;
        this._layer.destroyChildren();
        this._nodeMap = {};
        await this.updateBaseImage();
        await this.updateAttachmentLayers();
      },

      async updateBaseImage() {
        if (!this._layer || !this.currentCarrier) return;
        if (this._nodeMap.base) {
          this._nodeMap.base.destroy();
          delete this._nodeMap.base;
        }

        const imageSet = this.currentView === 'back'
          ? (this.currentCarrier.backImages || {})
          : (this.currentCarrier.baseImages || {});

        let url = null;
        for (const [key, val] of Object.entries(imageSet)) {
          if (colorMatch(key, this.selectedColor)) { url = val; break; }
        }
        if (!url) url = Object.values(imageSet)[0];
        if (!url) return;

        try {
          const img = await this.loadImage(url);
          const node = new Konva.Image({
            x: 0, y: 0,
            width: this.canvasWidth,
            height: this.canvasHeight,
            image: img,
          });
          this._nodeMap.base = node;
          this._layer.add(node);
          node.moveToBottom();
          this._layer.batchDraw();
        } catch { /* image failed to load */ }
      },

      async updateAttachmentLayers() {
        if (!this._layer || !this.currentCarrier) return;

        // Remove previous attachment/zone nodes
        for (const [key, node] of Object.entries(this._nodeMap)) {
          if (key.startsWith('att_') || key.startsWith('zone_')) {
            node.destroy();
            delete this._nodeMap[key];
          }
        }

        const visibleZones = this.currentZones;

        // Draw zone hotspot rectangles
        for (const zone of visibleZones) {
          const zx = (zone.x / 100) * this.canvasWidth;
          const zy = (zone.y / 100) * this.canvasHeight;
          const zw = (zone.width / 100) * this.canvasWidth;
          const zh = (zone.height / 100) * this.canvasHeight;
          const hasAtt = this.selectedAttachments.some(id => {
            const a = this.attachments.find(at => at.id === id);
            return a && a.zone === zone.id;
          });

          // Only show zone indicators for empty zones, hide when attachment is placed
          if (hasAtt) continue;
          const rect = new Konva.Rect({
            x: zx, y: zy, width: zw, height: zh,
            stroke: 'rgba(255,255,255,0.08)',
            strokeWidth: 1,
            dash: [4, 4],
            fill: 'transparent',
            cornerRadius: 4,
            listening: false,
          });
          this._nodeMap[`zone_${zone.id}`] = rect;
          this._layer.add(rect);
        }

        // Draw attachment overlays
        const visibleZoneIds = new Set(visibleZones.map(z => z.id));
        for (const attId of this.selectedAttachments) {
          const att = this.attachments.find(a => a.id === attId);
          if (!att || !visibleZoneIds.has(att.zone)) continue;
          const zone = visibleZones.find(z => z.id === att.zone);
          if (!zone) continue;

          const url = this.getOverlayUrl(att);
          if (!url) continue;

          try {
            const img = await this.loadImage(url);
            const zW = (zone.width / 100) * this.canvasWidth;
            const zH = (zone.height / 100) * this.canvasHeight;
            const zX = (zone.x / 100) * this.canvasWidth;
            const zY = (zone.y / 100) * this.canvasHeight;

            // Fit image into zone with padding, maintain aspect ratio
            const pad = 0.05; // 5% padding inside zone
            const innerW = zW * (1 - pad * 2);
            const innerH = zH * (1 - pad * 2);
            const scale = Math.min(innerW / img.naturalWidth, innerH / img.naturalHeight);
            const dW = img.naturalWidth * scale;
            const dH = img.naturalHeight * scale;
            const dX = zX + (zW - dW) / 2;
            const dY = zY + (zH - dH) / 2;

            const node = new Konva.Image({
              x: dX, y: dY, width: dW, height: dH, image: img,
            });
            this._nodeMap[`att_${attId}`] = node;
            this._layer.add(node);
          } catch { /* image failed to load */ }
        }

        this._layer.batchDraw();
      },

      // --- Cart ---

      async addToCart() {
        if (this.isAdding) return;
        this.isAdding = true;
        this.cartError = '';
        this.addedSuccess = false;

        const carrier = this.currentCarrier;
        if (!carrier) {
          this.cartError = 'Kein Plattenträger ausgewählt';
          this.isAdding = false;
          return;
        }

        const carrierVariant = carrier.variants.find(v =>
          colorMatch(v.option1, this.selectedColor) &&
          v.option2 === this.selectedSize &&
          v.available
        );

        if (!carrierVariant) {
          this.cartError = 'Variante nicht verfügbar';
          this.isAdding = false;
          setTimeout(() => { this.cartError = ''; }, 5000);
          return;
        }

        const items = [{ id: carrierVariant.id, quantity: 1 }];

        for (const att of this.selectedItems) {
          const attColor = this.getAttColor(att.id);
          const attVariant = att.variants.find(v =>
            colorMatch(v.option1, attColor) && v.available
          );
          if (attVariant) items.push({ id: attVariant.id, quantity: 1 });
        }

        try {
          const response = await fetch(this.cartAddUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
          });

          if (!response.ok) throw new Error('Cart error');

          this.addedSuccess = true;
          setTimeout(() => { this.addedSuccess = false; }, 3000);
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } catch {
          this.cartError = 'Fehler beim Hinzufügen zum Warenkorb';
          setTimeout(() => { this.cartError = ''; }, 5000);
        }

        this.isAdding = false;
      },
    };
  });
}

document.addEventListener('alpine:init', registerConfigurator);
if (typeof Alpine !== 'undefined') registerConfigurator();
